import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Calendar as CalendarIcon } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUS_STYLES = {
  scheduled: "bg-primary/10 text-primary",
  completed: "bg-secondary/15 text-secondary",
  cancelled: "bg-destructive/10 text-destructive",
  no_show: "bg-muted text-muted-foreground",
};

export default function PatientAppointments() {
  const [appointments, setAppointments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ doctor_id: "", date: "", reason: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [a, d] = await Promise.all([api.get("/patient/appointments"), api.get("/doctors", { params: { limit: 40 } })]);
    setAppointments(a.data);
    setDoctors(d.data);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.doctor_id || !form.date || !form.reason) {
      toast.error("Select a doctor, date, and reason");
      return;
    }
    setSaving(true);
    try {
      await api.post("/patient/appointments", form);
      toast.success("Appointment booked");
      setOpen(false);
      setForm({ doctor_id: "", date: "", reason: "" });
      load();
    } catch (e) {
      toast.error("Could not book appointment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell
      pageTitle="Appointments"
      pageDescription="Book, review, and manage every visit — with providers scored by our Trust Index."
      actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-md" data-testid="book-appointment-button"><Plus size={14} className="mr-1.5" /> Book appointment</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Book a consultation</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Doctor</Label>
                <Select value={form.doctor_id} onValueChange={(v) => setForm({ ...form, doctor_id: v })}>
                  <SelectTrigger data-testid="book-doctor-select"><SelectValue placeholder="Choose a doctor" /></SelectTrigger>
                  <SelectContent>
                    {doctors.map((d) => (
                      <SelectItem key={d.id} value={d.id} data-testid={`book-doctor-option-${d.id}`}>
                        {d.name} · {d.specialty} · {d.city} · Trust {d.trust_score}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="apt-date">Date & time</Label>
                <Input id="apt-date" type="datetime-local" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="book-date-input" />
              </div>
              <div>
                <Label htmlFor="apt-reason">Reason</Label>
                <Textarea id="apt-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Brief description of the concern" data-testid="book-reason-input" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} disabled={saving} data-testid="book-save-button">{saving ? "Booking…" : "Confirm booking"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      {appointments.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-10 text-center">
          <CalendarIcon size={28} className="mx-auto text-muted-foreground" />
          <div className="mt-3 font-serif text-xl text-primary">No appointments yet</div>
          <p className="text-sm text-muted-foreground mt-1">Book your first consultation.</p>
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-white overflow-hidden" data-testid="appointments-table">
          <table className="w-full text-sm">
            <thead className="bg-muted/60">
              <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Doctor</th>
                <th className="px-4 py-3">Specialty</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Reason</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {appointments.map((a) => (
                <tr key={a.id} className="border-t border-border" data-testid={`appointment-row-${a.id}`}>
                  <td className="px-4 py-3 font-medium text-primary">{a.doctor_name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{a.doctor_specialty}</td>
                  <td className="px-4 py-3">{formatDate(a.date)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{a.reason}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 ${STATUS_STYLES[a.status] || STATUS_STYLES.scheduled}`}>
                      {a.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AppShell>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}
